/**
 * Web Audio API Audio Mixer
 * 
 * Provides smooth audio playback for timeline preview with exact effect matching
 * to video_worker output. Applies all effects in the same order as FFmpeg:
 * 1. Trim (source-level trimming)
 * 2. Speed/Pitch (affects playback speed and pitch)
 * 3. EQ (equalization)
 * 4. Compression (dynamic range)
 * 5. Normalization (auto volume)
 * 6. Volume (manual volume)
 * 7. Fade (fade in/out)
 * 8. Pan (stereo positioning)
 * 9. Timeline Position (delay for timeline placement)
 */

class AudioMixer {
  constructor() {
    this.audioContext = null
    this.masterGain = null
    this.audioBuffers = new Map() // Cache for loaded audio buffers
    this.activeSources = new Map() // Map of item.id -> { source, nodes, scheduledTime }
    this.isInitialized = false
  }

  /**
   * Initialize Web Audio API context
   */
  async init() {
    if (this.isInitialized && this.audioContext && this.audioContext.state !== 'closed') {
      // Resume if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume()
      }
      return this.audioContext
    }

    try {
      // Create AudioContext (use webkit prefix for Safari compatibility)
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      this.audioContext = new AudioContextClass()
      
      // Create master gain node for output
      this.masterGain = this.audioContext.createGain()
      this.masterGain.gain.value = 1.0
      this.masterGain.connect(this.audioContext.destination)
      
      // Resume context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume()
      }
      
      this.isInitialized = true
      console.log('✅ AudioMixer initialized, state:', this.audioContext.state)
      return this.audioContext
    } catch (error) {
      console.error('❌ Failed to initialize AudioMixer:', error)
      throw error
    }
  }

  /**
   * Load and decode audio file to AudioBuffer
   */
  async loadAudioBuffer(url) {
    if (!url) {
      throw new Error('No URL provided')
    }

    // Check cache first
    if (this.audioBuffers.has(url)) {
      return this.audioBuffers.get(url)
    }

    try {
      console.log(`📥 Loading audio buffer: ${url}`)
      const response = await fetch(url, { 
        mode: 'cors',
        credentials: 'omit'
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`)
      }
      
      const arrayBuffer = await response.arrayBuffer()
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('Empty audio data received')
      }
      
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer)
      
      if (!audioBuffer) {
        throw new Error('Failed to decode audio data')
      }
      
      // Cache the buffer
      this.audioBuffers.set(url, audioBuffer)
      console.log(`✅ Loaded audio buffer: ${url} (${audioBuffer.duration.toFixed(2)}s, ${audioBuffer.numberOfChannels} channels)`)
      return audioBuffer
    } catch (error) {
      console.error(`❌ Failed to load audio buffer ${url}:`, error)
      throw error
    }
  }

  /**
   * Apply trim to audio buffer (creates sub-buffer)
   */
  applyTrimToBuffer(buffer, trimStart, trimEnd) {
    if (!trimStart && !trimEnd) {
      return buffer
    }

    const sampleRate = buffer.sampleRate
    const startSample = Math.floor((trimStart || 0) * sampleRate)
    const endSample = trimEnd ? Math.floor(trimEnd * sampleRate) : buffer.length
    
    if (startSample >= buffer.length || endSample <= startSample) {
      console.warn('⚠️ Invalid trim range, using full buffer')
      return buffer
    }

    // Create new buffer with trimmed length
    const trimmedLength = endSample - startSample
    const trimmedBuffer = this.audioContext.createBuffer(
      buffer.numberOfChannels,
      trimmedLength,
      sampleRate
    )

    // Copy trimmed portion to new buffer
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const originalData = buffer.getChannelData(channel)
      const trimmedData = trimmedBuffer.getChannelData(channel)
      for (let i = 0; i < trimmedLength; i++) {
        trimmedData[i] = originalData[startSample + i]
      }
    }

    return trimmedBuffer
  }

  /**
   * Create audio source node with full effect chain
   */
  createAudioSource(item, buffer) {
    if (!buffer || !buffer.length) {
      throw new Error('Invalid audio buffer')
    }
    
    // Apply trim first (before creating source)
    const trimStart = typeof item.trim?.start === 'number' && isFinite(item.trim.start) && item.trim.start >= 0 ? item.trim.start : 0
    const trimEnd = typeof item.trim?.end === 'number' && isFinite(item.trim.end) && item.trim.end > trimStart ? item.trim.end : null
    const trimmedBuffer = this.applyTrimToBuffer(buffer, trimStart, trimEnd)

    // Create source node from trimmed buffer
    const source = this.audioContext.createBufferSource()
    source.buffer = trimmedBuffer

    // Get audio effects
    const audioEffects = item.settings?.audioEffects || {}
    const settings = item.settings || {}

    // Build effect chain (matching video_worker order)
    let currentNode = source

    // 2. Speed/Pitch
    const speedConfig = audioEffects.speed || {}
    const speedValue = typeof speedConfig.value === 'number' && isFinite(speedConfig.value) && speedConfig.value > 0 ? speedConfig.value : 1.0
    if (speedValue !== 1.0) {
      source.playbackRate.value = Math.max(0.25, Math.min(4.0, speedValue)) // Clamp to valid range
    }

    const pitchConfig = audioEffects.pitch || {}
    const pitchSemitones = typeof pitchConfig.semitones === 'number' && isFinite(pitchConfig.semitones) ? pitchConfig.semitones : 0
    const maintainPitch = speedConfig.maintainPitch !== false
    if (pitchSemitones !== 0 && (speedValue === 1.0 || !maintainPitch)) {
      source.detune.value = Math.max(-1200, Math.min(1200, pitchSemitones * 100)) // Convert semitones to cents, clamp
    }

    // 3. EQ (Equalization)
    const eqConfig = audioEffects.eq || {}
    const lowGain = typeof eqConfig.low === 'number' && isFinite(eqConfig.low) ? eqConfig.low : 0
    const midGain = typeof eqConfig.mid === 'number' && isFinite(eqConfig.mid) ? eqConfig.mid : 0
    const highGain = typeof eqConfig.high === 'number' && isFinite(eqConfig.high) ? eqConfig.high : 0
    
    if (lowGain !== 0 || midGain !== 0 || highGain !== 0) {
      // Low shelf (Bass) at 80Hz
      if (lowGain !== 0) {
        const lowFilter = this.audioContext.createBiquadFilter()
        lowFilter.type = 'lowshelf'
        lowFilter.frequency.value = 80
        lowFilter.gain.value = Math.max(-40, Math.min(40, lowGain)) // Clamp to valid range
        currentNode.connect(lowFilter)
        currentNode = lowFilter
      }

      // Mid peak at 1000Hz
      if (midGain !== 0) {
        const midFilter = this.audioContext.createBiquadFilter()
        midFilter.type = 'peaking'
        midFilter.frequency.value = 1000
        midFilter.Q.value = 1.0
        midFilter.gain.value = Math.max(-40, Math.min(40, midGain)) // Clamp to valid range
        currentNode.connect(midFilter)
        currentNode = midFilter
      }

      // High shelf (Treble) at 8000Hz
      if (highGain !== 0) {
        const highFilter = this.audioContext.createBiquadFilter()
        highFilter.type = 'highshelf'
        highFilter.frequency.value = 8000
        highFilter.gain.value = Math.max(-40, Math.min(40, highGain)) // Clamp to valid range
        currentNode.connect(highFilter)
        currentNode = highFilter
      }
    }

    // 4. Compression
    const compressionConfig = audioEffects.compression || {}
    if (compressionConfig.enabled) {
      const compressor = this.audioContext.createDynamicsCompressor()
      const threshold = typeof compressionConfig.threshold === 'number' && isFinite(compressionConfig.threshold) ? compressionConfig.threshold : -12
      const ratio = typeof compressionConfig.ratio === 'number' && isFinite(compressionConfig.ratio) ? compressionConfig.ratio : 4
      const attack = typeof compressionConfig.attack === 'number' && isFinite(compressionConfig.attack) ? compressionConfig.attack : 5
      const release = typeof compressionConfig.release === 'number' && isFinite(compressionConfig.release) ? compressionConfig.release : 50
      
      compressor.threshold.value = Math.max(-100, Math.min(0, threshold))
      compressor.knee.value = 5
      compressor.ratio.value = Math.max(1, Math.min(20, ratio))
      compressor.attack.value = Math.max(0.0001, Math.min(1, attack / 1000)) // Convert ms to seconds, clamp
      compressor.release.value = Math.max(0.001, Math.min(1, release / 1000)) // Convert ms to seconds, clamp
      currentNode.connect(compressor)
      currentNode = compressor
    }

    // 5. Normalization
    const normalizationConfig = audioEffects.normalization || {}
    if (normalizationConfig.enabled) {
      // Calculate RMS and apply normalization gain
      const targetLevel = typeof normalizationConfig.targetLevel === 'number' && isFinite(normalizationConfig.targetLevel) ? normalizationConfig.targetLevel : -3
      const normalizationGain = this.calculateNormalizationGain(trimmedBuffer, targetLevel)
      const normGainNode = this.audioContext.createGain()
      // Ensure gain is finite and valid
      const validGain = isFinite(normalizationGain) ? Math.max(0, Math.min(10, normalizationGain)) : 1.0
      normGainNode.gain.value = validGain
      currentNode.connect(normGainNode)
      currentNode = normGainNode
    }

    // 6. Volume
    const volume = typeof settings.volume === 'number' && isFinite(settings.volume) ? settings.volume : 1.0
    const volumeGain = this.audioContext.createGain()
    volumeGain.gain.value = Math.max(0, Math.min(2, volume)) // Clamp to 0-2 range
    currentNode.connect(volumeGain)
    currentNode = volumeGain

    // 7. Fade
    const fadeIn = typeof settings.fadeIn === 'number' && isFinite(settings.fadeIn) && settings.fadeIn >= 0 ? settings.fadeIn : 0
    const fadeOut = typeof settings.fadeOut === 'number' && isFinite(settings.fadeOut) && settings.fadeOut >= 0 ? settings.fadeOut : 0
    const fadeGain = this.audioContext.createGain()
    if (fadeIn > 0 || fadeOut > 0) {
      // Fade will be applied via automation in playAudioItem
      currentNode.connect(fadeGain)
      currentNode = fadeGain
    } else {
      fadeGain.gain.value = 1.0
      currentNode.connect(fadeGain)
      currentNode = fadeGain
    }

    // 8. Pan
    const pan = typeof settings.pan === 'number' && isFinite(settings.pan) ? settings.pan : 0
    const panner = this.audioContext.createStereoPanner()
    panner.pan.value = Math.max(-1, Math.min(1, pan)) // Clamp to -1 to 1 range
    currentNode.connect(panner)
    currentNode = panner

    // Connect to master output
    currentNode.connect(this.masterGain)

    return {
      source,
      fadeGain,
      volumeGain,
      nodes: {
        source,
        fadeGain,
        volumeGain,
        panner
      }
    }
  }

  /**
   * Calculate normalization gain from buffer RMS
   */
  calculateNormalizationGain(buffer, targetLevelDB) {
    if (!buffer || buffer.length === 0) {
      return 1.0 // Return unity gain if buffer is invalid
    }
    
    // Calculate RMS
    let sumSquares = 0
    let sampleCount = 0
    
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel)
      for (let i = 0; i < channelData.length; i++) {
        const sample = channelData[i]
        if (isFinite(sample)) {
          sumSquares += sample * sample
          sampleCount++
        }
      }
    }
    
    if (sampleCount === 0) {
      return 1.0 // Return unity gain if no valid samples
    }
    
    const rms = Math.sqrt(sumSquares / sampleCount)
    if (!isFinite(rms) || rms <= 0) {
      return 1.0 // Return unity gain if RMS is invalid
    }
    
    const currentLevelDB = 20 * Math.log10(rms + 1e-10) // Avoid log(0)
    if (!isFinite(currentLevelDB)) {
      return 1.0 // Return unity gain if level is invalid
    }
    
    // Calculate gain needed to reach target level
    const gainDB = targetLevelDB - currentLevelDB
    if (!isFinite(gainDB)) {
      return 1.0 // Return unity gain if gainDB is invalid
    }
    
    const gainLinear = Math.pow(10, gainDB / 20)
    if (!isFinite(gainLinear) || gainLinear <= 0) {
      return 1.0 // Return unity gain if calculated gain is invalid
    }
    
    return Math.min(gainLinear, 10) // Limit gain to prevent clipping
  }

  /**
   * Play audio item at specific timeline position
   */
  playAudioItem(item, timelineStart, previewTime) {
    // Stop existing source for this item if any
    this.stopAudioItem(item.id)

    const itemStart = item.time?.start || 0
    const itemDuration = item.time?.duration || 0
    const itemEnd = itemStart + itemDuration

    // Check if item should be playing
    if (previewTime < itemStart || previewTime > itemEnd) {
      return
    }

    // Get audio URL
    let audioUrl = null
    if (item.type === 'tts') {
      audioUrl = item.tts_generated_link
    } else if (item.type === 'video') {
      // For video items, use the video URL directly
      // Web Audio API can decode audio from video files
      audioUrl = item.src || item.file?.url || item.url
      if (!audioUrl) {
        console.warn(`⚠️ Video item ${item.id} has no URL for audio extraction`)
        return
      }
      // Skip video items with stream URLs that might not work (e.g., /api/v1/videos/.../stream)
      if (audioUrl.includes('/stream') && !audioUrl.startsWith('http')) {
        console.warn(`⚠️ Skipping video item ${item.id} with stream URL (may not be accessible): ${audioUrl}`)
        return
      }
    } else {
      audioUrl = item.file?.url || item.url || item.src || item.file?.src
    }

    if (!audioUrl) {
      console.warn(`⚠️ No audio URL for item ${item.id}`)
      return
    }

    // Load and play audio
    this.loadAudioBuffer(audioUrl)
      .then(buffer => {
        if (!buffer) {
          console.error(`❌ No buffer loaded for item ${item.id}`)
          return
        }
        
        const { source, fadeGain, nodes } = this.createAudioSource(item, buffer)
        
        // Calculate when to start playback
        const relativeTime = previewTime - itemStart
        const trimStart = typeof item.trim?.start === 'number' && isFinite(item.trim.start) ? item.trim.start : 0
        const offset = relativeTime + trimStart
        
        // Calculate when to schedule (immediately or in future)
        const currentTime = this.audioContext.currentTime
        if (!isFinite(currentTime)) {
          console.error(`❌ Invalid AudioContext currentTime: ${currentTime}`)
          return
        }
        const when = currentTime + 0.01 // Small delay to ensure context is ready
        const startOffset = Math.max(0, Math.min(Math.max(0, offset), buffer.duration))
        
        // Validate all timing values
        if (!isFinite(when) || !isFinite(startOffset)) {
          console.error(`❌ Invalid timing values: when=${when}, startOffset=${startOffset}`)
          return
        }
        
        // Apply fade automation (use validated fade values from createAudioSource)
        const fadeIn = typeof item.settings?.fadeIn === 'number' && isFinite(item.settings.fadeIn) && item.settings.fadeIn >= 0 ? item.settings.fadeIn : 0
        const fadeOut = typeof item.settings?.fadeOut === 'number' && isFinite(item.settings.fadeOut) && item.settings.fadeOut >= 0 ? item.settings.fadeOut : 0
        const duration = typeof itemDuration === 'number' && isFinite(itemDuration) && itemDuration > 0 ? itemDuration : buffer.duration
        
        if (fadeIn > 0 && isFinite(fadeIn)) {
          const fadeInEnd = when + fadeIn
          if (isFinite(fadeInEnd)) {
            fadeGain.gain.setValueAtTime(0, when)
            fadeGain.gain.linearRampToValueAtTime(1.0, fadeInEnd)
          } else {
            fadeGain.gain.setValueAtTime(1.0, when)
          }
        } else {
          fadeGain.gain.setValueAtTime(1.0, when)
        }
        
        if (fadeOut > 0 && isFinite(fadeOut) && duration > fadeOut && isFinite(duration)) {
          const fadeOutStart = when + (duration - fadeOut)
          const fadeOutEnd = when + duration
          if (isFinite(fadeOutStart) && isFinite(fadeOutEnd)) {
            fadeGain.gain.setValueAtTime(1.0, fadeOutStart)
            fadeGain.gain.linearRampToValueAtTime(0, fadeOutEnd)
          }
        }
        
        // Schedule playback
        try {
          if (!isFinite(when) || !isFinite(startOffset) || startOffset < 0) {
            console.error(`❌ Cannot start audio: invalid timing (when=${when}, startOffset=${startOffset})`)
            return
          }
          source.start(when, startOffset)
          
          // Store active source
          this.activeSources.set(item.id, {
            source,
            nodes,
            scheduledTime: when,
            item
          })
          
          console.log(`✅ Playing audio item ${item.id} (${item.name || item.type}) at offset ${startOffset.toFixed(2)}s, buffer duration: ${buffer.duration.toFixed(2)}s`)
        } catch (error) {
          console.error(`❌ Failed to start audio source for item ${item.id}:`, error)
        }
      })
      .catch(error => {
        console.error(`❌ Failed to play audio item ${item.id} (${item.name || item.type}):`, error)
        console.error('  Audio URL:', audioUrl)
      })
  }

  /**
   * Stop audio item
   */
  stopAudioItem(itemId) {
    const activeSource = this.activeSources.get(itemId)
    if (activeSource) {
      try {
        activeSource.source.stop()
      } catch (error) {
        // Source may already be stopped
      }
      this.activeSources.delete(itemId)
    }
  }

  /**
   * Seek all audio sources to timeline position
   */
  seekAllAudio(items, previewTime) {
    // Stop all current sources
    this.activeSources.forEach((activeSource, itemId) => {
      try {
        activeSource.source.stop()
      } catch (error) {
        // Ignore errors
      }
    })
    this.activeSources.clear()

    // Restart all items that should be playing
    items.forEach(item => {
      this.playAudioItem(item, 0, previewTime)
    })
  }

  /**
   * Update playback for all items based on timeline position
   */
  async updatePlayback(items, isPlaying, previewTime) {
    if (!this.isInitialized) {
      console.warn('⚠️ AudioMixer not initialized')
      return
    }

    // Resume context if suspended (required by some browsers)
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume()
        console.log('✅ AudioContext resumed')
      } catch (error) {
        console.error('❌ Failed to resume AudioContext:', error)
      }
    }

    if (isPlaying) {
      // Check if we need to seek (timeline jumped)
      let needsSeek = false
      const currentActiveItems = new Set(this.activeSources.keys())
      const shouldBeActiveItems = new Set()
      
      items.forEach(item => {
        const itemStart = item.time?.start || 0
        const itemEnd = itemStart + (item.time?.duration || 0)
        const isInRange = previewTime >= itemStart && previewTime <= itemEnd
        
        if (isInRange) {
          shouldBeActiveItems.add(item.id)
          const activeSource = this.activeSources.get(item.id)
          
          // Check if item needs to be restarted (seeking or not playing)
          if (!activeSource) {
            needsSeek = true
          } else {
            // Check if we're significantly out of sync (more than 0.1s)
            const itemStart = item.time?.start || 0
            const relativeTime = previewTime - itemStart
            const trimStart = item.trim?.start || 0
            const expectedOffset = relativeTime + trimStart
            
            // For active sources, we can't easily check current position
            // So we'll restart if there's a significant time jump
            const timeSinceStart = this.audioContext.currentTime - activeSource.scheduledTime
            const timeDiff = Math.abs(timeSinceStart - relativeTime)
            if (timeDiff > 0.1) {
              needsSeek = true
            }
          }
        }
      })
      
      // If items changed or we need to seek, restart all
      const itemsChanged = currentActiveItems.size !== shouldBeActiveItems.size ||
        Array.from(currentActiveItems).some(id => !shouldBeActiveItems.has(id))
      
      if (needsSeek || itemsChanged) {
        console.log(`🔄 Restarting audio playback (needsSeek: ${needsSeek}, itemsChanged: ${itemsChanged})`)
        // Stop all current sources
        this.activeSources.forEach((activeSource, itemId) => {
          this.stopAudioItem(itemId)
        })
        
        // Start all items that should be playing
        const itemsToPlay = items.filter(item => {
          const itemStart = item.time?.start || 0
          const itemEnd = itemStart + (item.time?.duration || 0)
          return previewTime >= itemStart && previewTime <= itemEnd
        })
        
        console.log(`🎵 Found ${itemsToPlay.length} audio item(s) to play at time ${previewTime.toFixed(2)}s`)
        itemsToPlay.forEach(item => {
          this.playAudioItem(item, 0, previewTime)
        })
      } else {
        // Log active sources for debugging
        if (this.activeSources.size > 0) {
          console.log(`🎵 ${this.activeSources.size} audio source(s) active`)
        } else {
          console.log(`⚠️ No audio sources active, but isPlaying=${isPlaying}, previewTime=${previewTime.toFixed(2)}s`)
        }
      }
    } else {
      // Pause all
      this.activeSources.forEach((activeSource, itemId) => {
        this.stopAudioItem(itemId)
      })
    }
  }

  /**
   * Cleanup all audio sources and context
   */
  cleanup() {
    // Stop all sources
    this.activeSources.forEach((activeSource, itemId) => {
      try {
        activeSource.source.stop()
      } catch (error) {
        // Ignore errors
      }
    })
    this.activeSources.clear()

    // Close context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close()
    }

    this.isInitialized = false
    console.log('🧹 AudioMixer cleaned up')
  }
}

// Export singleton instance
const audioMixer = new AudioMixer()
export default audioMixer

